import { cn, getAuthClient, useAuth } from '@xhubio-saas/core-frontend/kernel'
import { Camera, Loader2, User } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface AvatarUploadProps {
  className?: string
  disabled?: boolean
}

const MAX_AVATAR_BYTES = 512 * 1024 // 512 KB — avatars are tiny

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsDataURL(file)
  })
}

/**
 * Avatar upload component.
 *
 * Stores the image as a base64 data-URL on the Better-Auth `user.image`
 * field via `authClient.updateUser({ image })`. This is the v1 approach:
 * fits all small avatars in the user row, no separate storage service
 * needed. For larger avatars or CDN delivery, swap this out for a
 * server-side upload endpoint.
 */
export function AvatarUpload({ className, disabled = false }: AvatarUploadProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleClick = () => {
    if (disabled || uploading) return
    inputRef.current?.click()
  }

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setError(null)

    // Errors go through t(): these were hard-coded German, so every
    // non-German UI showed German the moment an upload failed.
    if (!file.type.startsWith('image/')) {
      setError(t('profile.avatarInvalidType', 'Please choose an image.'))
      return
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setError(
        t('profile.avatarTooLarge', {
          maxKb: Math.round(MAX_AVATAR_BYTES / 1024),
          defaultValue: 'File too large (max. {{maxKb}} KB).',
        })
      )
      return
    }

    try {
      setUploading(true)
      const dataUrl = await fileToDataUrl(file)
      const authClient = getAuthClient()
      await authClient.updateUser({ image: dataUrl })
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('profile.avatarUploadFailed', 'Upload failed.')
      )
    } finally {
      setUploading(false)
      // Clear the input so re-uploading the same file fires `onChange`.
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className={cn('relative', className)}>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className={cn(
          'relative h-20 w-20 rounded-full border-2 border-dashed transition-colors overflow-hidden',
          disabled
            ? 'border-muted-foreground/15 cursor-not-allowed opacity-50'
            : 'border-muted-foreground/25 hover:border-muted-foreground/50'
        )}
      >
        {user?.image ? (
          <img
            src={user.image}
            alt={user.name || 'Avatar'}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-muted">
            <User className="h-8 w-8 text-muted-foreground" />
          </div>
        )}

        {/* Overlay - only show when not disabled */}
        {!disabled && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity">
            {uploading ? (
              <Loader2 className="h-6 w-6 animate-spin text-white" />
            ) : (
              <Camera className="h-6 w-6 text-white" />
            )}
          </div>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleChange}
        className="hidden"
        disabled={disabled || uploading}
      />

      {error && (
        <p className="mt-2 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
